import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["query", "search"]
  static values  = {
    debounce:  { type: Number, default: 300 },
    lockDelay: { type: Number, default: 200 },
  }

  initialize() {
    this.debounceTimer = null
    this.lockTimer = null
    this.locked = []
  }

  connect() {
    this.syncUrlParamsAsHiddenInputs()
    this.#observeFrameBusy()
    this.boundReapplyAfterMorph = this.syncUrlParamsAsHiddenInputs.bind(this)
    document.addEventListener("turbo:morph", this.boundReapplyAfterMorph)
  }

  disconnect() {
    this.busyObserver?.disconnect()
    clearTimeout(this.lockTimer)
    if (this.boundReapplyAfterMorph) document.removeEventListener("turbo:morph", this.boundReapplyAfterMorph)
  }

  #observeFrameBusy() {
    const frameId = this.element.dataset.turboFrame
    this.frame = frameId && document.getElementById(frameId)
    if (!this.frame) return

    this.busyObserver = new MutationObserver(() => this.#syncBusy())
    this.busyObserver.observe(this.frame, {
      attributes: true,
      attributeFilter: ["busy"],
    })
    this.#syncBusy()
  }

  #syncBusy() {
    if (this.frame.hasAttribute("busy")) {
      if (this.lockTimer || this.locked?.length) return
      this.lockTimer = setTimeout(() => this.#lock(), this.lockDelayValue)
    } else {
      clearTimeout(this.lockTimer)
      this.lockTimer = null
      this.#unlock()
    }
  }

  #lock() {
    this.lockTimer = null
    this.locked = this.#elementsToLock()
    this.locked.forEach((el) => {
      el.inert = true
      el.classList.add("is-busy-locked")
    })
    this.element.setAttribute("aria-busy", "true")
    this.#setPopoversInert(true)
  }

  #unlock() {
    this.locked?.forEach((el) => {
      el.inert = false
      el.classList.remove("is-busy-locked")
    })
    this.locked = []
    this.element.removeAttribute("aria-busy")
    this.#setPopoversInert(false)
  }

  #elementsToLock() {
    const exempt = this.#focusedSearchField()
    if (!exempt) return [this.element]

    const toLock = []
    for (let node = exempt; node !== this.element; node = node.parentElement) {
      for (const sibling of node.parentElement.children) {
        if (sibling !== node) toLock.push(sibling)
      }
    }
    return toLock
  }

  #focusedSearchField() {
    if (!this.hasSearchTarget || !this.element.contains(this.searchTarget)) return null
    return this.searchTarget.contains(document.activeElement) ? this.searchTarget : null
  }

  #setPopoversInert(locked) {
    this.frame
      ?.querySelectorAll("[data-controller~='column-filter']")
      .forEach((el) => { el.inert = locked })
  }

  schedule() {
    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.submit(), this.debounceValue)
  }

  submit() {
    this.syncUrlParamsAsHiddenInputs()
    this.element.requestSubmit()
  }

  syncOnSubmit() {
    this.syncUrlParamsAsHiddenInputs()
  }

  syncUrlParamsAsHiddenInputs() {
    this.element
      .querySelectorAll('input[type="hidden"][data-filters-mirrored="true"]')
      .forEach((el) => el.remove())

    const formNames = new Set(
      Array.from(this.element.elements).map((el) => el.name).filter(Boolean)
    )

    const params = new URLSearchParams(window.location.search)
    for (const [name, value] of params) {
      if (name === "page") continue
      if (formNames.has(name)) continue
      const input = document.createElement("input")
      input.type  = "hidden"
      input.name  = name
      input.value = value
      input.dataset.filtersMirrored = "true"
      this.element.appendChild(input)
    }
  }
}
